import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckIcon, UsersIcon } from "lucide-react";

import type { APIError } from "simplydo/core/api";
import { asMutation, initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H1, H2, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageContent } from "simplydo/react/ui/message";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

type RedeemablePersistentToken = NonNullable<
  OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]["persistentToken"]
>;

const PersistentToken = () => {
  const { t } = useTranslation();
  const [loggingOutForHostOrg, setLoggingOutForHostOrg] = useState(false);

  const user = useCurrentUser();
  const navigate = useNavigate();

  const { token } = useParams<{ token: string }>();

  const getEntityAction = (persistentToken: RedeemablePersistentToken) => {
    const { forEntity } = persistentToken;
    if (forEntity === "invitation") {
      return t("generic.invited");
    }
    return "";
  };

  const getForTypeText = (persistentToken: RedeemablePersistentToken) => {
    const { forType } = persistentToken;
    if (forType === "ideaBusinessProfile") {
      return t("persistentToken.joinBusinessProfile");
    } else if (forType === "idea") {
      return t("persistentToken.joinIdea");
    } else if (forType === "group") {
      return t("persistentToken.joinGroup");
    }
    return "";
  };

  const getEntityName = (persistentToken: RedeemablePersistentToken) => {
    const { forType } = persistentToken;
    if (forType === "ideaBusinessProfile") {
      const { ownerIdeaBusinessProfile } = persistentToken;
      return ownerIdeaBusinessProfile?.name ?? t("generic.unknown");
    } else if (forType === "idea") {
      const { ownerIdea } = persistentToken;
      return ownerIdea?.name ?? t("generic.unknown");
    } else if (forType === "group") {
      const { ownerGroup } = persistentToken;
      return ownerGroup?.name ?? t("generic.unknown");
    }
    return "";
  };

  const getActionText = (persistentToken: RedeemablePersistentToken) => {
    const { forEntity } = persistentToken;
    if (forEntity === "invitation") {
      return t("persistentToken.acceptInvitation");
    }
    return "";
  };

  // If we have data indicating the user has previously visited this link, and created an entity/accepted an invitation, redirect them to the entity
  const handleRedirect = useCallback(
    (
      persistentToken: OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]["persistentToken"],
    ) => {
      if (persistentToken.forEntity === "invitation") {
        if (persistentToken.forType === "ideaBusinessProfile") {
          navigate(`/?businessProfileId=${persistentToken.forId}`); // See AutoBusinessProfileModal.tsx
        } else if (persistentToken.forType === "idea") {
          navigate(`/ideas/${persistentToken.forId}`);
        } else if (persistentToken.forType === "group") {
          navigate(`/groups/${persistentToken.forId}`);
        }
      }
    },
    [navigate],
  );

  const persistentTokenQuery = useQuery(
    initUseQuery(api.persistentTokens.getByToken, [token!], {
      isEnabled: !!token,
      onSuccess: (res) => {
        if (res.userHasRedeemedToken) {
          handleRedirect(res.persistentToken);
        }
      },
    }),
  );

  const persistentToken = persistentTokenQuery.data?.persistentToken ?? null;
  const ownerOrganisation =
    persistentTokenQuery.data?.ownerOrganisation ?? null;

  const redeemMutation = useMutation({
    mutationFn: asMutation(api.persistentTokens.redeem)!,
    onSuccess: () => {
      toast.success("Invitation accepted");
      if (persistentToken) {
        api.invalidateQueries(api.users.me).then(() => {
          handleRedirect(persistentToken);
        });
      }
    },
  });

  const redeemError = redeemMutation.error as APIError | null;
  const redeemErrorIsWrongOrg = redeemError?.errorCode === "WRONG_ORGANISATION";
  const hostOrgId = ownerOrganisation?._id;
  const userInHostOrganisation =
    !hostOrgId ||
    !user ||
    user.organisation === hostOrgId ||
    (user.organisations ?? []).includes(hostOrgId);
  const isWrongOrganisation =
    redeemErrorIsWrongOrg ||
    (!!user &&
      !!hostOrgId &&
      !userInHostOrganisation &&
      (persistentToken?.forType === "idea" ||
        persistentToken?.forType === "group"));

  const entityTypeKey =
    persistentToken?.forType === "group" ? "generic.group" : "generic.idea";

  const queryErrorMessage = persistentTokenQuery.error?.message || "";
  const redeemErrorMessage =
    redeemError && !isWrongOrganisation ? redeemError.message : "";
  const errorMessage = redeemErrorMessage || queryErrorMessage;

  const goToHostOrganisationLogin = () => {
    if (!token || !ownerOrganisation || loggingOutForHostOrg) return;

    const loginUrl = util.getBaseUrl(
      ownerOrganisation as OpenAPI.Schemas["Organisation"],
      `/login?then=/join/${token}`,
    );

    setLoggingOutForHostOrg(true);
    api.auth.logout(
      () => {
        window.location.href = loginUrl;
      },
      () => {
        // Mirror navbar: still clear local session if the request fails, then
        // continue so the user can sign in to the host organisation.
        api.options?.onLogout?.();
        window.location.href = loginUrl;
      },
    );
  };

  return (
    <div style={{ paddingBottom: 5 }}>
      {(errorMessage || isWrongOrganisation) && (
        <>
          <H1>{t("generic.invitations")}</H1>
          <Divider />
        </>
      )}

      {persistentToken ? (
        <>
          <H2 style={{ textAlign: "center" }}>
            <Icon icon={UsersIcon} />

            {t("generic.joinType", { type: getEntityName(persistentToken) })}
            <Subheader>
              {persistentToken.ownerUser ? (
                <UserChip user={persistentToken.ownerUser} compact="very" />
              ) : (
                t("generic.someone")
              )}
              {` ${t("persistentToken.generic.has")} `}
              {getEntityAction(persistentToken)}
              {` ${t("persistentToken.generic.youTo")} `}
              {getForTypeText(persistentToken)}
            </Subheader>
          </H2>
        </>
      ) : null}

      {isWrongOrganisation ? (
        <>
          <Message color="red">
            <MessageContent>
              {t("persistentToken.error.wrongOrganisation", {
                type: t(entityTypeKey),
                organisation: ownerOrganisation?.name ?? t("generic.unknown"),
              })}
            </MessageContent>
          </Message>
          <Divider />
          <p>{t("persistentToken.error.wrongOrganisationHint")}</p>
          <Button
            onClick={goToHostOrganisationLogin}
            disabled={!ownerOrganisation || loggingOutForHostOrg}
            loading={loggingOutForHostOrg}
            style={{ marginBottom: 5 }}
            color="primary"
          >
            {t("persistentToken.error.continueToOrganisation", {
              organisation: ownerOrganisation?.name ?? t("generic.unknown"),
            })}
          </Button>
        </>
      ) : null}

      {errorMessage ? (
        <>
          <Message color="red">
            <MessageContent>
              {errorMessage}. {t("persistentToken.linkInvalidMessage")}
            </MessageContent>
          </Message>
          <Divider />
          <p>{t("persistentToken.error.loginMessage")}</p>
          <ButtonLink style={{ marginBottom: 5 }} color="primary" to={`/login`}>
            {t("entry.login.loginOrSignUp")}
          </ButtonLink>
        </>
      ) : null}

      {persistentTokenQuery.isLoading ? <Loader /> : null}

      {persistentToken ? (
        user ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 35,
              marginTop: 20,
              width: "100%",
            }}
          >
            {!isWrongOrganisation ? (
              <Button
                fluid
                loading={redeemMutation.isPending}
                onClick={() => {
                  if (!token) return;
                  redeemMutation.mutate([token]);
                }}
                style={{ maxWidth: 500 }}
                disabled={!!errorMessage}
                color="primary"
              >
                <Icon icon={CheckIcon} />
                {t("invitation.accept")}
              </Button>
            ) : null}
          </div>
        ) : (
          <>
            <Message style={{ marginTop: 25 }} color="blue">
              {t("persistentToken.loginToAccept", {
                action: getActionText(persistentToken),
              })}
            </Message>
            <ButtonLink
              style={{ float: "right" }}
              color="primary"
              to={`/login?then=/join/${token}`}
            >
              {t("entry.login.loginOrSignUp")}
            </ButtonLink>
          </>
        )
      ) : null}
    </div>
  );
};

export default PersistentToken;
